import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  HStack,
  Text,
  Spinner,
  useColorModeValue,
  Fade
} from '@chakra-ui/react';
import { IconType } from 'react-icons';
import { MdSelectAll } from 'react-icons/md';
import { FiX } from 'react-icons/fi';

export interface BulkAction {
  label: string;
  colorScheme: string;
  icon: IconType;
  onClick: () => void;
  confirmLabel?: string;
  confirmColor?: string;
}

export interface BulkSelectionBarProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  actions: BulkAction[];
  onCancel: () => void;
  isLoading?: boolean;
  entityName?: string;
}

export default function BulkSelectionBar({
  selectedCount,
  totalCount,
  onSelectAll,
  actions,
  onCancel,
  isLoading = false,
  entityName = 'item'
}: BulkSelectionBarProps) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const [confirmAction, setConfirmAction] = useState<BulkAction | null>(null);

  const allSelected = selectedCount === totalCount && totalCount > 0;

  const handleActionClick = (action: BulkAction) => {
    setConfirmAction(action);
  };

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction.onClick();
      setConfirmAction(null);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmAction(null);
  };

  const handleCancelSelection = () => {
    setConfirmAction(null);
    onCancel();
  };

  return (
    <Box
      position="fixed"
      bottom="24px"
      left="50%"
      transform="translateX(-50%)"
      zIndex={99}
    >
      <Card
        bg={bgColor}
        borderColor={borderColor}
        borderWidth="1px"
        shadow="lg"
        borderRadius="lg"
      >
        <CardBody py={3} px={4}>
          {confirmAction ? (
            // Confirmation state
            <Fade in={true}>
              <HStack spacing={4}>
                <Text
                  fontWeight="medium"
                  color={confirmAction.confirmColor || `${confirmAction.colorScheme}.600`}
                >
                  {confirmAction.confirmLabel || confirmAction.label} {selectedCount} {entityName}
                  {selectedCount !== 1 ? 's' : ''}?
                </Text>

                <Button
                  size="sm"
                  colorScheme={confirmAction.colorScheme}
                  leftIcon={isLoading ? <Spinner size="xs" /> : <confirmAction.icon />}
                  onClick={handleConfirm}
                  isDisabled={isLoading}
                >
                  Confirm
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<FiX />}
                  onClick={handleCancelConfirm}
                  isDisabled={isLoading}
                >
                  Back
                </Button>
              </HStack>
            </Fade>
          ) : (
            // Selection state
            <HStack spacing={4}>
              <Text fontWeight="medium" minW="100px">
                {selectedCount} selected
              </Text>

              <Button
                size="sm"
                variant="outline"
                leftIcon={<MdSelectAll />}
                onClick={onSelectAll}
                isDisabled={isLoading || allSelected}
              >
                {allSelected ? 'All Selected' : 'Select All'}
              </Button>

              {actions.map((action, index) => (
                <Button
                  key={index}
                  size="sm"
                  colorScheme={action.colorScheme}
                  leftIcon={<action.icon />}
                  onClick={() => handleActionClick(action)}
                  isDisabled={selectedCount === 0 || isLoading}
                >
                  {action.label}
                </Button>
              ))}

              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FiX />}
                onClick={handleCancelSelection}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
            </HStack>
          )}
        </CardBody>
      </Card>
    </Box>
  );
}
